import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, GitMerge, TrendingUp, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';

// ── Mini sparkline chart shown on strategy card ───────────────────────────────
function MiniChart({ data, color = '#00FF94', height = 64 }) {
  if (!data || data.length < 2) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 16, height: 16, border: '2px solid #27272A', borderTopColor: color, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );
  const values = data.map(d => d.value);
  const minV = Math.min(...values), maxV = Math.max(...values);
  const range = maxV - minV || 1;
  const w = 300, pad = 3, chartH = height - pad * 2;
  const getX = i => (i / (data.length - 1)) * w;
  const getY = v => pad + chartH - ((v - minV) / range) * chartH;
  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(d.value).toFixed(1)}`).join(' ');
  const area = line + ` L ${getX(data.length-1).toFixed(1)},${(pad+chartH).toFixed(1)} L 0,${(pad+chartH).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`mg${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35}/>
          <stop offset="100%" stopColor={color} stopOpacity={0.02}/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#mg${color.replace('#','')})`}/>
      <path d={line} stroke={color} strokeWidth={1.8} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Blend preview chart ────────────────────────────────────────────────────────
function BlendChart({ data, height = 180 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.value);
  const minVal = Math.min(...values), maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  const pad = 10, chartH = height - pad * 2, w = 400;
  const getX = i => (i / (data.length - 1)) * w;
  const getY = v => pad + chartH - ((v - minVal) / range) * chartH;
  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(d.value).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L ${getX(data.length-1).toFixed(1)},${(pad+chartH).toFixed(1)} L 0,${(pad+chartH).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id="blendGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2979FF" stopOpacity={0.4}/>
          <stop offset="100%" stopColor="#2979FF" stopOpacity={0.02}/>
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((pct, i) => {
        const y = (pad + chartH * pct).toFixed(1);
        return <line key={i} x1="0" y1={y} x2={w} y2={y} stroke="rgba(39,39,42,0.4)" strokeDasharray="3 3"/>;
      })}
      <path d={areaPath} fill="url(#blendGradient)"/>
      <path d={linePath} stroke="#2979FF" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const FILTER_TABS = ['all', 'active', 'backtesting', 'community'];

export default function StrategiesPage() {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [filter, setFilter] = useState('all');
  // mini chart data per strategy: { strategy_id: [{value}] }
  const [miniCharts, setMiniCharts] = useState({});
  const [loading, setLoading] = useState(true);
  const [bundleMode, setBundleMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [blendResult, setBlendResult] = useState(null);
  const [blendLoading, setBlendLoading] = useState(false);
  const [startingBalance, setStartingBalance] = useState('100000');

  useEffect(() => {
    Promise.all([
      api.getStrategies().catch(() => []),
      api.getPersonalStrategy().catch(() => null),
    ]).then(([d, personal]) => {
      // Personal strategy pinned to the top of the list — private to the
      // current trader, never visible to anyone else.
      const merged = personal ? [personal, ...d] : d;
      setStrategies(merged);
      d.forEach(strat => {
        api.getStrategyEquityCurve(strat.strategy_id, 'weekly')
          .then(curve => {
            const data = curve?.data || [];
            const slice = data.slice(-12);
            setMiniCharts(prev => ({ ...prev, [strat.strategy_id]: slice }));
          })
          .catch(() => {});
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? strategies : filter === 'community' ? strategies.filter(s => s.is_user_created) : strategies.filter(s => s.status === filter);

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handlePreviewBundle = async () => {
    if (!selectedIds.length) return;
    setBlendLoading(true);
    setBlendResult(null);
    setShowBundleModal(true);
    try {
      const result = await api.blendStrategies({ strategy_ids: selectedIds, starting_balance: parseFloat(startingBalance) || 10000, granularity: 'monthly' });
      setBlendResult(result);
    } catch (e) { console.error(e); }
    setBlendLoading(false);
  };

  const blendChartMin = useMemo(() => {
    if (!blendResult?.blended_curve?.length) return 0;
    return Math.max(0, Math.floor(Math.min(...blendResult.blended_curve.map(p => p.value)) * 0.95));
  }, [blendResult]);

  if (loading) {
    return <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}><div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '0.1em' }}>STRATEGIES</h1>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', marginTop: 4 }}>{strategies.length} Trading Systems</p>
        </div>
        {!bundleMode ? (
          <button data-testid="bundle-calculator-btn" onClick={() => setBundleMode(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(41,121,255,0.1)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            <GitMerge size={16} /> Bundle
          </button>
        ) : (
          <button data-testid="cancel-bundle-btn" onClick={() => { setBundleMode(false); setSelectedIds([]); }} style={{ padding: '8px 14px', background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        )}
      </div>

      {/* Bundle Banner */}
      {bundleMode && (
        <div style={{ margin: '0 16px 12px', background: 'rgba(41,121,255,0.08)', border: '1px solid rgba(41,121,255,0.25)', padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Check size={18} color="var(--pp-accent)" />
            <span style={{ fontSize: 14, color: 'var(--pp-accent)', fontWeight: 600 }}>
              {selectedIds.length === 0 ? 'Tap strategies to select' : `${selectedIds.length} selected`}
            </span>
          </div>
          <button data-testid="select-all-btn" onClick={() => selectedIds.length === strategies.length ? setSelectedIds([]) : setSelectedIds(strategies.map(s => s.strategy_id))} style={{ background: 'none', border: 'none', color: 'var(--pp-accent)', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
            {selectedIds.length === strategies.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      )}

      {/* Filters */}
      <div data-testid="strategies-filter-chips" style={{ display: 'flex', gap: 8, padding: '0 16px', marginBottom: 16 }}>
        {FILTER_TABS.map(f => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} style={{ padding: '7px 16px', background: filter === f ? 'rgba(41,121,255,0.12)' : 'transparent', border: `1px solid ${filter === f ? 'var(--pp-accent)' : 'var(--pp-border)'}`, color: filter === f ? 'var(--pp-accent)' : 'var(--pp-text-3)', fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer' }}>{f}</button>
        ))}
      </div>

      {/* Strategy Cards */}
      {filtered.map(strat => {
        const isSelected = selectedIds.includes(strat.strategy_id);
        const isActive = strat.status === 'active';
        return (
          <button
            key={strat.strategy_id}
            data-testid={`strategy-card-${strat.strategy_id}`}
            onClick={() => bundleMode ? toggleSelect(strat.strategy_id) : navigate(`/strategy/${strat.strategy_id}`)}
            style={{ width: 'calc(100% - 32px)', margin: '0 16px 12px', background: isSelected ? 'rgba(41,121,255,0.05)' : 'var(--pp-surface)', border: `1px solid ${isSelected ? 'var(--pp-accent)' : 'var(--pp-border)'}`, textAlign: 'left', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
          >
            {bundleMode && (
              <div style={{ position: 'absolute', top: 14, right: 14, width: 22, height: 22, background: isSelected ? 'var(--pp-accent)' : 'transparent', border: `2px solid ${isSelected ? 'var(--pp-accent)' : 'var(--pp-border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                {isSelected && <Check size={14} color="#000" />}
              </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 16px 10px' }}>
              <div style={{ flex: 1, paddingRight: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '0.05em' }}>{strat.name}</p>
                  {strat.requires_approval && (
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: '#FFB000', background: 'rgba(255,176,0,0.15)', border: '1px solid rgba(255,176,0,0.3)', padding: '2px 7px' }}>INTERVIEW REQ.</span>
                  )}
                  {strat.is_user_created && (
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: '#00C8FF', background: 'rgba(0,200,255,0.15)', border: '1px solid rgba(0,200,255,0.3)', padding: '2px 7px' }}>COMMUNITY</span>
                  )}
                </div>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{strat.instrument} · {strat.timeframe}</p>
              </div>
              <span className={isActive ? 'badge-success' : 'badge-warning'}>{(strat.status || '').toUpperCase()}</span>
            </div>

            {/* Mini sparkline chart — 3M weekly view */}
            <div style={{ height: 72, background: '#050505', borderTop: '1px solid var(--pp-border)', borderBottom: '1px solid var(--pp-border)', overflow: 'hidden' }}>
              <MiniChart
                data={miniCharts[strat.strategy_id]}
                color={isActive ? '#00FF94' : '#2979FF'}
                height={72}
              />
            </div>

            {/* 3-metric row: All Time Return · Monthly Avg · Max Drawdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr' }}>
              <div style={{ textAlign: 'center', padding: '10px 6px' }}>
                <p className="mono" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#00FF94' }}>+{strat.total_return_pct?.toFixed(0)}%</p>
                <p style={{ margin: '2px 0 0', fontSize: 8, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>ALL TIME</p>
              </div>
              <div style={{ background: 'var(--pp-border)' }} />
              <div style={{ textAlign: 'center', padding: '10px 6px' }}>
                <p className="mono" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#2979FF' }}>{strat.monthly_avg_return_pct?.toFixed(1) || strat.monthly_return_pct?.toFixed(1)}%<span style={{ fontSize: 10, color: 'var(--pp-text-3)' }}>/mo</span></p>
                <p style={{ margin: '2px 0 0', fontSize: 8, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>AVG MONTHLY</p>
              </div>
              <div style={{ background: 'var(--pp-border)' }} />
              <div style={{ textAlign: 'center', padding: '10px 6px' }}>
                <p className="mono" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#FF3333' }}>{strat.max_drawdown_pct}%</p>
                <p style={{ margin: '2px 0 0', fontSize: 8, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>MAX DD</p>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '8px 14px', borderTop: '1px solid var(--pp-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>{strat.win_rate}% Win</span>
                <span style={{ color: 'var(--pp-border)' }}>·</span>
                <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>
                  Live since {strat.live_since
                    ? new Date(strat.live_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : (strat.backtest_period || strat.live_period || '').split(/[–-]/)[0]?.trim() || '—'}
                </span>
              </div>
              {!bundleMode && <ChevronRight size={16} color="var(--pp-text-3)" />}
            </div>
          </button>
        );
      })}

      <div style={{ height: 40 }} />

      {/* Sticky Footer */}
      {bundleMode && selectedIds.length > 0 && (
        <div style={{ position: 'fixed', bottom: 64, left: 0, right: 0, background: 'var(--pp-surface)', borderTop: '1px solid var(--pp-border)', padding: '14px 20px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 40 }}>
          <div style={{ flex: 1, marginRight: 16, overflow: 'hidden' }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{selectedIds.length} strategies selected</p>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {strategies.filter(s => selectedIds.includes(s.strategy_id)).map(s => s.name).join(' + ')}
            </p>
          </div>
          <button data-testid="preview-bundle-btn" onClick={handlePreviewBundle} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--pp-accent)', border: 'none', color: '#000', padding: '10px 16px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            <TrendingUp size={16} /> Preview Bundle
          </button>
        </div>
      )}

      {/* Bundle Modal */}
      {showBundleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 600, background: 'var(--pp-surface)', borderTopLeftRadius: 0, borderTopRightRadius: 0, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--pp-border)', flexShrink: 0 }}>
              <div>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Portfolio Bundle</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', marginTop: 2 }}>{selectedIds.length} strategies combined</p>
              </div>
              <button data-testid="close-bundle-modal-btn" onClick={() => { setShowBundleModal(false); setBundleMode(false); setSelectedIds([]); }} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', padding: 4 }}><X size={24} /></button>
            </div>

            <div style={{ overflowY: 'auto', padding: 24 }}>
              {blendLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 12 }}>
                  <div style={{ width: 32, height: 32, border: '2px solid var(--pp-accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <p style={{ color: 'var(--pp-text-3)', fontSize: 14 }}>Calculating blend...</p>
                </div>
              ) : blendResult ? (
                <>
                  {/* Balance input */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--pp-overlay)', padding: '12px 14px', marginBottom: 16 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pp-text-2)' }}>Starting Balance</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="mono" style={{ fontSize: 18, color: 'var(--pp-text-3)' }}>$</span>
                      <input
                        data-testid="starting-balance-input"
                        type="number"
                        value={startingBalance}
                        onChange={e => setStartingBalance(e.target.value)}
                        onBlur={handlePreviewBundle}
                        style={{ width: 100, fontSize: 20, fontWeight: 700, textAlign: 'right', padding: '4px 8px' }}
                      />
                    </div>
                  </div>

                  {/* Hero */}
                  <div style={{ background: 'var(--pp-overlay)', padding: 20, textAlign: 'center', marginBottom: 16 }}>
                    <p className="section-label" style={{ marginBottom: 6 }}>Projected Final Balance</p>
                    <p className="mono" style={{ fontSize: 36, fontWeight: 800, color: 'var(--pp-success)', margin: '6px 0' }}>${blendResult.final_balance?.toLocaleString()}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <TrendingUp size={16} color="var(--pp-success)" />
                      <span className="mono" style={{ color: 'var(--pp-success)', fontWeight: 600 }}>+{blendResult.blended_return_pct?.toFixed(1)}% blended return</span>
                    </div>
                  </div>

                  {/* Chart */}
                  {blendResult.blended_curve?.length > 1 && (
                    <div style={{ marginBottom: 16 }}>
                      <p className="section-label" style={{ marginBottom: 8 }}>Blended Equity Curve</p>
                      <BlendChart data={blendResult.blended_curve} height={180} />
                    </div>
                  )}

                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                    {[
                      { label: 'AVG WIN RATE', value: `${blendResult.stats?.avg_win_rate?.toFixed(1)}%`, color: 'var(--pp-success)' },
                      { label: 'AVG RETURN', value: `${blendResult.stats?.avg_return_pct?.toFixed(1)}%`, color: 'var(--pp-accent)' },
                      { label: 'AVG DRAWDOWN', value: `${blendResult.stats?.avg_drawdown_pct?.toFixed(1)}%`, color: 'var(--pp-error)' },
                    ].map(s => (
                      <div key={s.label} style={{ background: 'var(--pp-overlay)', padding: 12, textAlign: 'center' }}>
                        <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</p>
                        <p className="section-label" style={{ marginTop: 4 }}>{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Strategy list */}
                  <div style={{ background: 'var(--pp-overlay)', padding: 14, marginBottom: 16 }}>
                    <p className="section-label" style={{ marginBottom: 12 }}>Strategies in Bundle</p>
                    {blendResult.stats?.strategy_names?.map((n, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(39,39,42,0.5)' }}>
                        <div style={{ width: 8, height: 8, background: 'var(--pp-accent)', borderRadius: '50%', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{n}</span>
                        <span className="mono" style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>{Math.round(100 / blendResult.stats.strategy_count)}%</span>
                      </div>
                    ))}
                  </div>

                  <button data-testid="start-all-strategies-btn" onClick={() => { setShowBundleModal(false); setBundleMode(false); setSelectedIds([]); }} className="btn-primary" style={{ width: '100%', height: 52 }}>EXPLORE STRATEGIES TO SUBSCRIBE</button>

                  <p style={{ fontSize: 10, color: 'var(--pp-text-3)', textAlign: 'center', marginTop: 12, lineHeight: 1.6 }}>* Projections are based on historical strategy performance. Past results do not guarantee future returns.</p>
                </>
              ) : (
                <p style={{ color: 'var(--pp-error)', textAlign: 'center', paddingTop: 40 }}>Could not load blend data. Please try again.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
